// UI appearance preference. Mirrors auth.tsx's provider/hook shape.
//
// 'editorial' is the original Rev9 editorial design (top nav, teal/copper,
// generous spacing). 'clean' is a professional SaaS fund-accounting skin
// (left sidebar, light slate surfaces, indigo accent, one-screen pages).
//
// The active appearance is written to <html data-view="clean"> so CSS can
// flip everything via the [data-view="clean"] override layer in globals.css,
// and persisted to localStorage so a refresh keeps the choice. main.tsx
// reads the same key pre-paint to avoid a flash of the wrong appearance.

import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

export type Appearance = 'editorial' | 'clean';

// v2: the original key persisted 'editorial' on every load (when editorial
// was the default), pinning returning users to editorial after the default
// flipped to clean. Bumping the key ignores that stale value so everyone
// falls through to the new clean default; explicit toggles still persist.
export const APPEARANCE_STORAGE_KEY = 'rev9_appearance_v2';

function applyToDocument(appearance: Appearance) {
  if (appearance === 'clean') {
    document.documentElement.dataset.view = 'clean';
  } else {
    delete document.documentElement.dataset.view;
  }
}

function readStored(): Appearance {
  // Clean (professional SaaS sidebar) is the default; editorial is opt-in.
  try {
    return localStorage.getItem(APPEARANCE_STORAGE_KEY) === 'editorial' ? 'editorial' : 'clean';
  } catch {
    return 'clean';
  }
}

interface PreferencesContextValue {
  appearance: Appearance;
  setAppearance: (a: Appearance) => void;
  toggleAppearance: () => void;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [appearance, setAppearanceState] = useState<Appearance>(readStored);

  useEffect(() => {
    applyToDocument(appearance);
    try { localStorage.setItem(APPEARANCE_STORAGE_KEY, appearance); }
    catch { /* private mode — in-memory only, fine */ }
  }, [appearance]);

  const value: PreferencesContextValue = {
    appearance,
    setAppearance: setAppearanceState,
    toggleAppearance: () => setAppearanceState(a => (a === 'clean' ? 'editorial' : 'clean')),
  };

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesContextValue {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used inside <PreferencesProvider>');
  return ctx;
}
